import { Badge, CreateBadgeRequest, AwardBadgeRequest } from '../Types/badge';
import {
	getAllBadges,
	getActiveBadges,
	getBadge,
	createBadge,
	updateBadge,
	deleteBadge,
	toggleBadgeActive,
	getBadgesWithUserStatus,
	awardBadge,
	removeBadge,
} from '../Services/badge';
import { useBizQuery, useBizMutation } from './useBizQuery';

const QUERYKEY_ALL_BADGES = 'ALL_BADGES';
const QUERYKEY_ACTIVE_BADGES = 'ACTIVE_BADGES';
const QUERYKEY_ONE_BADGE = 'ONE_BADGE';
const QUERYKEY_USER_BADGES = 'USER_BADGES';
const QUERYKEY_BADGES_WITH_USER_STATUS = 'BADGES_WITH_USER_STATUS';

// Badge queries
export const useGetAllBadges = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_ALL_BADGES], () => getAllBadges());
	return { badges: data, statusState };
};

export const useGetActiveBadges = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_ACTIVE_BADGES], () => getActiveBadges());
	return { badges: data, statusState };
};

export const useGetBadge = (badgeId: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_ONE_BADGE, badgeId.toString()], () => getBadge(badgeId));
	return { badge: data, statusState };
};

export const useGetBadgesWithUserStatus = (userId?: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_BADGES_WITH_USER_STATUS, userId?.toString() || ''], () =>
		getBadgesWithUserStatus(userId)
	);
	return { badges: data, statusState };
};

// Badge mutations
export const useCreateBadge = () => {
	const wrapperFn = (params: { badge: CreateBadgeRequest }) => {
		return createBadge(params.badge);
	};
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ALL_BADGES], [QUERYKEY_ACTIVE_BADGES]],
	});

	return {
		createBadge: (badge: CreateBadgeRequest) => mutate({ badge }),
		result: data,
		statusState,
	};
};

export const useUpdateBadge = () => {
	const wrapperFn = (params: { badge: Badge }) => {
		return updateBadge(params.badge);
	};
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ALL_BADGES], [QUERYKEY_ACTIVE_BADGES], [QUERYKEY_ONE_BADGE]],
	});

	return {
		updateBadge: (badge: Badge) => mutate({ badge }),
		result: data,
		statusState,
	};
};

export const useDeleteBadge = () => {
	const wrapperFn = (params: { badgeId: number }) => {
		return deleteBadge(params.badgeId);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ALL_BADGES], [QUERYKEY_ACTIVE_BADGES], [QUERYKEY_ONE_BADGE]],
	});

	return {
		deleteBadge: (badgeId: number) => mutate({ badgeId }),
		statusState,
	};
};

export const useToggleBadgeActive = () => {
	const wrapperFn = (params: { badgeId: number }) => {
		return toggleBadgeActive(params.badgeId);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ALL_BADGES], [QUERYKEY_ACTIVE_BADGES], [QUERYKEY_ONE_BADGE]],
	});

	return {
		toggleBadgeActive: (badgeId: number) => mutate({ badgeId }),
		statusState,
	};
};

// UserBadge mutations
export const useAwardBadge = () => {
	const wrapperFn = (params: { request: AwardBadgeRequest }) => {
		return awardBadge(params.request);
	};
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_USER_BADGES], [QUERYKEY_BADGES_WITH_USER_STATUS]],
	});

	return {
		awardBadge: (request: AwardBadgeRequest) => mutate({ request }),
		result: data,
		statusState,
	};
};

export const useRemoveBadge = () => {
	const wrapperFn = (params: { userBadgeId: number }) => {
		return removeBadge(params.userBadgeId);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_USER_BADGES], [QUERYKEY_BADGES_WITH_USER_STATUS]],
	});

	return {
		removeBadge: (userBadgeId: number) => mutate({ userBadgeId }),
		statusState,
	};
};
